/**
 * WatermarkCanvas.ts
 *
 * 一个用于在背景图片上使用 Canvas 绘制水印（文字或图片）的 TypeScript 小类。
 *
 * 功能：
 * - 支持文字水印和图片水印。
 * - 配置图片水印的大小（宽/高），如果只指定一侧则保持纵横比。
 * - 支持在背景上的相对位置配置（命名位置或归一化 {x,y} 在 [0,1] 区间）。
 * - 支持配置距离边缘的像素偏移（offsetX, offsetY）。
 * - 支持配置水印的不透明度和旋转。
 * - 适用于浏览器环境（使用 HTMLCanvasElement / HTMLImageElement）。
 *
 * 浏览器使用示例：
 * const wm = new WatermarkCanvas({ width: 1200, height: 800 });
 * const canvas = await wm.drawBackgroundAndWatermarks('bg.jpg', [
 *   { type: 'text', text: 'Demo Watermark', font: '28px sans-serif', color: 'rgba(255,255,255,0.9)', position: 'bottom-right', offsetX: 24, offsetY: 24, opacity: 0.9 },
 *   { type: 'image', imageSrc: 'logo.png', imageWidth: 120, position: { x: 0.02, y: 0.02 }, offsetX: 0, offsetY: 0, opacity: 0.9 }
 * ]);
 * document.body.appendChild(canvas);
 */
import { reportEntryLog } from '@xt/client/utils/log'

type PositionKeyword = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' | 'top' | 'bottom' | 'left' | 'right'

export type RelPosition = { x: number; y: number } // 归一化坐标：0..1

export interface BaseWatermarkOptions {
  // 指定水印放置的位置。可接受命名关键字或归一化坐标 { x: 0..1, y: 0..1 }
  position?: PositionKeyword | RelPosition

  // 相对于由 position 指定的边缘的像素偏移。
  // 对于归一化或居中位置，这些偏移作为像素偏移直接加到最终坐标上。
  offsetX?: number // 默认 0
  offsetY?: number // 默认 0

  // 水印不透明度 0..1
  opacity?: number // 默认 1

  // 以水印中心为旋转中心的旋转角度（度数）
  rotation?: number // 默认 0
}

export interface TextWatermarkOptions extends BaseWatermarkOptions {
  type: 'text'
  text: string
  font?: string // 例如 '24px sans-serif'
  color?: string // 例如 'rgba(255,255,255,0.8)'
  maxWidth?: number // 可选：绘制时的最大宽度（用于截断/换行）
  textAlign?: CanvasTextAlign // 默认 'left'
  textBaseline?: CanvasTextBaseline // 默认 'alphabetic'
}

export interface ImageWatermarkOptions extends BaseWatermarkOptions {
  type: 'image'
  imageSrc: string | HTMLImageElement
  // 水印期望的宽/高（像素）。如果只指定一项则按原始纵横比计算另一项。
  imageWidth?: number
  imageHeight?: number
}

export type WatermarkOptions = TextWatermarkOptions | ImageWatermarkOptions

export interface WatermarkCanvasInit {
  width: number
  height: number
  // 用于在高 DPI 显示器上渲染更清晰的图像的设备像素比。默认使用 window.devicePixelRatio（若可用），否则为 1。
  dpr?: number
  // 可选：当没有背景图片或部分区域为空时使用的背景填充颜色
  backgroundColor?: string
}

function isPositionKeyword(p?: PositionKeyword | RelPosition): p is PositionKeyword {
  return typeof p === 'string'
}

function toRadians(deg: number) {
  return (deg * Math.PI) / 180
}

// 获取url的文件名和扩展名
export function getFileNameAndExt(url: string) {
  const reg = /[^/]+(?=\.?($|\?|#))/
  const matchResult = url.match(reg)
  const imgExt = matchResult ? matchResult[0].split('.').pop()?.toLowerCase() || 'jpeg' : 'png'
  const filename = matchResult ? matchResult[0] : `default.${imgExt}`

  return { name: filename, imgExt }
}

// 从 src 加载图片（字符串 URL）或如果传入的是 HTMLImageElement 则直接返回该元素
export function loadImage(src: string | HTMLImageElement): Promise<HTMLImageElement> {
  if (src instanceof HTMLImageElement) {
    if (src.complete) return Promise.resolve(src)
    return new Promise((resolve, reject) => {
      src.onload = () => resolve(src)
      src.onerror = e => reject(new Error('Image load error'))
    })
  }
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // 允许 CORS，以便尽可能让 toDataURL 可用
    img.onload = () => resolve(img)
    img.onerror = e => {
      reject(new Error(`Failed to load image: ${src}`))
      reportEntryLog('content.load-pic.fail', e)
    }
    img.src = src
  })
}

export class WatermarkCanvas {
  width: number
  height: number
  dpr: number

  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D

  constructor(init: WatermarkCanvasInit) {
    this.dpr = typeof init.dpr === 'number' ? init.dpr : typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
    this.width = init.width
    this.height = init.height
    this.initCanvas()
  }

  initCanvas() {
    this.canvas = document.createElement('canvas')
    this.canvas.width = Math.round(this.width * this.dpr)
    this.canvas.height = Math.round(this.height * this.dpr)

    const ctx = this.canvas.getContext('2d')

    if (!ctx) throw new Error('Cannot get 2D context from canvas')

    this.ctx = ctx

    this.ctx.scale(this.dpr, this.dpr) // 按 dpr 缩放绘制操作
  }

  // 暴露 canvas 元素
  getCanvas(): HTMLCanvasElement {
    return this.canvas
  }

  // 获取指定字体样式下的文本尺寸
  measureTextSize(
    text: string,
    fontStyle: {
      fontSize: number
      lineHeight: number
    }
  ): { width: number; height: number } {
    this.ctx.save()

    if (!text) return { width: 0, height: 0 }

    this.ctx.font = `normal ${fontStyle.fontSize}px Arial`
    this.ctx.textBaseline = 'middle'
    const metrics = this.ctx.measureText(text)
    const width = metrics.width

    this.ctx.restore()
    return { width, height: fontStyle.lineHeight }
  }

  // 将背景图片按保持比例缩放并填充到画布（cover 行为）
  private drawBackgroundImage(img: HTMLImageElement) {
    const canvasW = this.width
    const canvasH = this.height
    const iw = img.naturalWidth
    const ih = img.naturalHeight
    if (iw === 0 || ih === 0) return

    const scale = Math.max(canvasW / iw, canvasH / ih)
    const sw = canvasW / scale
    const sh = canvasH / scale
    const sx = (iw - sw) / 2
    const sy = (ih - sh) / 2

    console.log('drawBgImage', this.width, canvasW, canvasH)

    this.ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasW, canvasH)
  }

  // 计算给定水印尺寸和位置配置后的最终左上角坐标
  private computePosition(
    pos: PositionKeyword | RelPosition | undefined,
    watermarkW: number,
    watermarkH: number,
    offsetX = 0,
    offsetY = 0
  ): { x: number; y: number } {
    // 默认居中
    let nx = 0.5,
      ny = 0.5

    if (!pos) {
      nx = 0.5
      ny = 0.5
    } else if (isPositionKeyword(pos)) {
      switch (pos) {
        case 'top-left':
          nx = 0
          ny = 0
          break
        case 'top':
          nx = 0.5
          ny = 0
          break
        case 'top-right':
          nx = 1
          ny = 0
          break
        case 'left':
          nx = 0
          ny = 0.5
          break
        case 'center':
          nx = 0.5
          ny = 0.5
          break
        case 'right':
          nx = 1
          ny = 0.5
          break
        case 'bottom-left':
          nx = 0
          ny = 1
          break
        case 'bottom':
          nx = 0.5
          ny = 1
          break
        case 'bottom-right':
          nx = 1
          ny = 1
          break
        default:
          nx = 0.5
          ny = 0.5
      }
    } else {
      // 归一化坐标
      nx = Math.max(0, Math.min(1, pos.x))
      ny = Math.max(0, Math.min(1, pos.y))
    }

    // 计算画布参考点
    const refX = nx * this.width
    const refY = ny * this.height

    // 对于边缘位置，offsetX/Y 被解释为从该边缘向内的距离。
    // 放置规则：
    // - nx == 0 => 左边对齐，x = offsetX
    // - nx == 1 => 右边对齐，x = width - offsetX - watermarkW
    // - nx == 0.5 => 水平居中 + offsetX
    let x: number
    if (nx === 0) x = offsetX
    else if (nx === 1) x = this.width - offsetX - watermarkW
    else x = refX - watermarkW / 2 + offsetX

    let y: number
    if (ny === 0) y = offsetY
    else if (ny === 1) y = this.height - offsetY - watermarkH
    else y = refY - watermarkH / 2 + offsetY

    return { x, y }
  }

  // 绘制单个水印配置项
  private async drawSingleWatermark(opt: WatermarkOptions) {
    const opacity = typeof opt.opacity === 'number' ? opt.opacity : 1
    const rotation = typeof opt.rotation === 'number' ? opt.rotation : 0
    const offsetX = typeof opt.offsetX === 'number' ? opt.offsetX : 0
    const offsetY = typeof opt.offsetY === 'number' ? opt.offsetY : 0
    const pos = opt.position ?? 'center'

    if (opt.type === 'text') {
      const font = opt.font ?? '18px sans-serif'
      const color = opt.color ?? 'rgba(0,0,0,0.6)'
      const text = opt.text ?? ''
      const textAlign = opt.textAlign ?? 'left'
      const textBaseline = opt.textBaseline ?? 'alphabetic'

      this.ctx.save()
      this.ctx.globalAlpha = opacity
      this.ctx.font = font
      this.ctx.fillStyle = color
      this.ctx.textAlign = textAlign
      this.ctx.textBaseline = textBaseline

      this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
      this.ctx.shadowBlur = 1
      this.ctx.shadowOffsetY = 1
      this.ctx.shadowOffsetX = 1

      // 测量文本框
      const metrics = this.ctx.measureText(text)
      // 近似从 font 中估算高度（没有可靠的高度度量时使用）
      const fontSizeMatch = /(\d+)px/.exec(font)
      const estimatedHeight = fontSizeMatch
        ? parseInt(fontSizeMatch[1], 10)
        : parseInt(metrics.fontBoundingBoxAscent?.toString() || '18', 10)

      // 计算矩形的宽高（用于定位）
      const w = Math.ceil(metrics.width)
      const h = Math.ceil(estimatedHeight)

      // 先把水印视为一个 w x h 的矩形并计算其左上角，然后根据 textAlign/textBaseline 调整绘制点
      const base = this.computePosition(pos, w, h, offsetX, offsetY)
      let tx = base.x
      let ty = base.y

      // 根据文本对齐方式计算绘制点
      let drawX = tx
      if (textAlign === 'left') drawX = tx
      else if (textAlign === 'center') drawX = tx + w / 2
      else if (textAlign === 'right') drawX = tx + w

      let drawY = ty
      // 基线映射：
      if (textBaseline === 'top') drawY = ty
      else if (textBaseline === 'middle') drawY = ty + h / 2
      else if (textBaseline === 'alphabetic' || textBaseline === 'ideographic' || textBaseline === 'bottom') drawY = ty + h
      else drawY = ty + h // 备用

      if (rotation !== 0) {
        // 围绕水印中心旋转
        const cx = tx + w / 2
        const cy = ty + h / 2
        this.ctx.translate(cx, cy)
        this.ctx.rotate(toRadians(rotation))
        this.ctx.translate(-cx, -cy)
      }

      console.log('drawText', drawX, drawY)

      this.ctx.fillText(text, drawX, drawY, opt.maxWidth)
      this.ctx.restore()
      this.ctx.shadowColor = 'transparent'
      this.ctx.shadowBlur = 0
      this.ctx.shadowOffsetX = 0
      this.ctx.shadowOffsetY = 0
    } else if (opt.type === 'image') {
      // 加载图片
      const img = await loadImage(opt.imageSrc)
      const iw = img.naturalWidth
      const ih = img.naturalHeight
      if (iw === 0 || ih === 0) return

      let targetW = typeof opt.imageWidth === 'number' ? opt.imageWidth : undefined
      let targetH = typeof opt.imageHeight === 'number' ? opt.imageHeight : undefined

      if (targetW && !targetH) {
        targetH = Math.round((targetW * ih) / iw)
      } else if (!targetW && targetH) {
        targetW = Math.round((targetH * iw) / ih)
      } else if (!targetW && !targetH) {
        // 默认使用原始尺寸并限制到画布宽度
        targetW = Math.min(iw, this.width)
        targetH = Math.round((targetW * ih) / iw)
      }

      const w = targetW!
      const h = targetH!

      const { x, y } = this.computePosition(pos, w, h, offsetX, offsetY)

      this.ctx.save()

      this.ctx.globalAlpha = opacity

      if (rotation !== 0) {
        const cx = x + w / 2
        const cy = y + h / 2
        this.ctx.translate(cx, cy)
        this.ctx.rotate(toRadians(rotation))
        this.ctx.translate(-cx, -cy)
      }

      this.ctx.drawImage(img, x, y, w, h)
      this.ctx.restore()
    }
  }

  // 主方法：绘制背景（图片或填充）以及一个或多个水印
  // background 是HTMLImageElement。
  // watermarks：单个或数组形式的 WatermarkOptions。
  // 返回画布元素。
  async drawBackgroundAndWatermarks(
    bgImg: HTMLImageElement,
    watermarks?: WatermarkOptions | WatermarkOptions[]
  ): Promise<HTMLCanvasElement> {
    // 清空画布
    this.ctx.clearRect(0, 0, this.width, this.height)

    // 如果提供了背景图片则绘制
    try {
      this.drawBackgroundImage(bgImg)
    } catch (e) {
      // 如果加载失败，保留 backgroundColor（或空白）
      console.warn('Failed to load background image:', e)
      reportEntryLog('content.load-pic-bgpic.fail', e)
    }

    if (!watermarks) return this.canvas

    const arr = Array.isArray(watermarks) ? watermarks : [watermarks]

    // 依次绘制每个水印
    for (const w of arr) {
      try {
        await this.drawSingleWatermark(w)
      } catch (e) {
        console.warn('Failed to draw watermark', e)
      }
    }

    return this.canvas
  }

  // 便捷方法：获取数据 URL（默认 PNG）
  toDataURL(type = 'image/png', quality?: any): string {
    return this.canvas.toDataURL(type, quality)
  }
}

/*
注意：
- 对于跨域图片，请确保远程服务器允许跨域请求（CORS），否则画布会被污染（tainted），toDataURL 将不可用。
- 该类默认使用 devicePixelRatio 来在高 DPI 显示器上获得更清晰的渲染。
*/
